import { useEffect, useState } from 'react';
import { useFormik } from 'formik';

import { initialValues, validationSchema } from './formData';
import { AlertsProcessProps } from './props';

import { Alerts } from '../../../../../../shared/types/legalProceedings/alerts';
import { ParamEnum, ISelect } from '../../../../../../shared/types/shared';
import { asyncGetParams } from '../../../../../../services/param.service';

export const useAlertsStatus = ({
    data,
    onChangeData,
    onChangeFormik,
}: AlertsProcessProps) => {
    const [typeStatus, setTypeStatus] = useState<ISelect[]>([]);
    const [typeNewStatus, setTypeNewStatus] = useState<ISelect[]>([]);
    const [receptionChannels, setReceptionChannels] = useState<ISelect[]>([]);
    const [typePQR, setTypePQR] = useState<ISelect[]>([]);
    const [receivedThroughs, setReceivedThroughs] = useState<ISelect[]>([]);
    const [subtypeDps, setSubtypeDps] = useState<ISelect[]>([]);
    const [transactions, setTypesTransaction] = useState<ISelect[]>([]);
    const [hasNewStatus, setHasNewStatus] = useState<boolean>(false);

    const onSubmit = (data: Alerts) => {
        if (onChangeData) onChangeData(data || {});
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit,
    });

    useEffect(() => {
        if (onChangeFormik) onChangeFormik(formik);
    }, [formik.values, formik.errors]);

    useEffect(() => {
        if (data) {
            formik?.setFieldValue(
                'status',
                (data?.status as ISelect)?.id || ''
            );
            formik?.setFieldValue(
                'newStatus',
                (data?.newStatus as ISelect)?.id || ''
            );
            if ((data?.newStatus as ISelect)?.id) {
                setHasNewStatus(true);
            }
            formik?.setFieldValue(
                'receptionChannel',
                (data?.receptionChannel as ISelect)?.id || ''
            );
            formik?.setFieldValue(
                'typePQR',
                (data?.typePQR as ISelect)?.id || ''
            );
            formik?.setFieldValue(
                'receivedThrough',
                (data?.receivedThrough as ISelect)?.id || ''
            );
            formik?.setFieldValue(
                'subtypeDP',
                (data?.subtypeDP as ISelect)?.id || ''
            );
            formik?.setFieldValue(
                'transaction',
                (data?.transaction as ISelect)?.id || ''
            );
            formik?.setFieldValue('name', data?.name || '');
            formik?.setFieldValue('active', data?.active || true);
            formik?.setFieldValue('observation', data?.observation || '');
        }
    }, [data]);

    useEffect(() => {
        asyncGetParams(ParamEnum.PQR_RECEPTION_CHANNEL).then((items) =>
            setReceptionChannels(items)
        );
        asyncGetParams(ParamEnum.PQR_TYPE).then((items) => setTypePQR(items));
        asyncGetParams(ParamEnum.PQR_STATUS).then((params) =>
            setTypeStatus(params)
        );
        asyncGetParams(ParamEnum.PQR_PROCEDURE).then((params) =>
            setTypesTransaction(params)
        );
    }, []);

    useEffect(() => {
        if (Boolean(formik.values.typePQR)) {
            asyncGetParams(
                ParamEnum.PQR_HALF,
                undefined,
                formik.values.typePQR
            ).then((items) => {
                setReceivedThroughs(items);
            });
        }
        if (!data || !Boolean(data.receivedThrough)) {
            setReceivedThroughs([]);
            setSubtypeDps([]);
            formik?.setFieldValue('receivedThrough', '');
            formik?.setFieldValue('subtypeDP', '');
        }
    }, [formik.values.typePQR, typePQR]);

    useEffect(() => {
        if (Boolean(formik.values.receivedThrough)) {
            asyncGetParams(
                ParamEnum.PQR_SUBTYPE_DP,
                undefined,
                formik.values.receivedThrough
            ).then((items) => setSubtypeDps(items));
        }
        if (!data || !Boolean(data.subtypeDP)) {
            setSubtypeDps([]);
            formik?.setFieldValue('subtypeDP', '');
        }
    }, [formik.values.receivedThrough, receivedThroughs]);

    const getNameFromId = (
        idOrIds: string | ISelect | (string | ISelect)[] | undefined,
        options: ISelect[]
    ): string | undefined => {
        if (Array.isArray(idOrIds)) {
            return idOrIds
                .map((item) => {
                    if (typeof item === 'string') {
                        return (
                            options.find((opt) => opt.id === item)?.name ?? item
                        );
                    } else {
                        return item.name;
                    }
                })
                .join(', ');
        }
        if (typeof idOrIds === 'string') {
            return options.find((opt) => opt.id === idOrIds)?.name ?? idOrIds;
        }
        return idOrIds?.name;
    };

    useEffect(() => {
        let receivedThroughName: string | undefined = '';
        let subtypeDPName: string | undefined = '';
        const receptionChannelName = getNameFromId(
            formik.values.receptionChannel,
            receptionChannels
        );

        const statusName = getNameFromId(formik.values.status, typeStatus);
        const typePQRName = getNameFromId(formik.values.typePQR, typePQR);
        if (formik.values.typePQR) {
            receivedThroughName = getNameFromId(
                formik.values.receivedThrough,
                receivedThroughs
            );
            if (formik.values.receivedThrough) {
                subtypeDPName = getNameFromId(
                    formik.values.subtypeDP,
                    subtypeDps
                );
            }
        }
        const transactionName = getNameFromId(
            formik.values.transaction,
            transactions
        );
        const nameParts = [
            statusName,
            receptionChannelName,
            typePQRName,
            receivedThroughName,
            subtypeDPName,
            transactionName,
        ].filter((part) => part);

        const name = nameParts.join(' | ');

        if (name) formik.setFieldValue('name', name);
    }, [
        formik.values.receptionChannel,
        formik.values.receivedThrough,
        formik.values.typePQR,
        formik.values.subtypeDP,
        formik.values.transaction,
        formik.values.status,
        receptionChannels,
        typePQR,
        receivedThroughs,
        subtypeDps,
        transactions,
        typeStatus,
    ]);

    useEffect(() => {
        const statusParam = typeStatus.find(
            (s) => s.id === formik.values.status
        );
        const statusSeg = typeStatus.find(
            (s) => s.code === `${ParamEnum.PQR_STATUS}_SEG`
        );

        if (
            statusParam &&
            (statusParam.code === `${ParamEnum.PQR_STATUS}_TRA` ||
                statusParam.code === `${ParamEnum.PQR_STATUS}_PRO`)
        ) {
            setHasNewStatus(true);
            setTypeNewStatus(statusSeg ? [statusSeg] : []);
            formik?.setFieldValue('newStatus', statusSeg ? statusSeg.id : '');
            formik?.setFieldValue('canSendEmail', false);
        } else {
            setHasNewStatus(false);
            setTypeNewStatus([]);
            formik?.setFieldValue('newStatus', undefined);
            formik?.setFieldValue('canSendEmail', true);
        }
    }, [formik.values.status]);

    return {
        formik,
        typeStatus,
        receptionChannels,
        typePQR,
        receivedThroughs,
        subtypeDps,
        transactions,
        hasNewStatus,
        typeNewStatus,
    };
};
